import { useFormikContext } from "formik";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { VStack } from "@design-system/VStack/VStack";
import type { ApplyCloseTemplateWizardValues } from "./applyCloseTemplate.schema";
import { useScheduleWizardPreviewOccurrences } from "./useScheduleWizardPreviewOccurrences";
import { ServiceTemplateScheduleOccurrencesTable } from "./ServiceTemplateScheduleOccurrencesTable";
import { SCHEDULE_TEMPLATE_STEPS } from "./scheduleTemplateStep.constants";
import { ScheduleTemplateStepServicePeriod } from "./ScheduleTemplateStepServicePeriod";
import { ScheduleTemplateStepSchedule } from "./ScheduleTemplateStepSchedule";
import { ScheduleTemplateStepWork } from "./ScheduleTemplateStepWork";
import styles from "./ScheduleWizardStepDefaults.module.scss";

interface Props {
    /** Returns to step 1 (choose apply type). */
    onBack: () => void;
    /** Advances the schedule wizard to the team step. */
    onContinue: () => void;
}

export const ScheduleWizardStepDefaults = ({ onBack, onContinue }: Props) => {
    const formik = useFormikContext<ApplyCloseTemplateWizardValues>();
    const schedule = formik.values.defaults.schedule;
    const occurrenceRows = useScheduleWizardPreviewOccurrences(schedule);

    return (
        <div className={styles.wrapper}>
            <div className={styles.subtitleContainer}>
                Step 2 of 5 · Occurrences
            </div>
            <div className={styles.container}>
                <div className={styles.left}>
                    <VStack gap={16} className={styles.leftFormContent}>
                        <section className={styles.formSection}>
                            <VStack gap={16}>
                                <VStack gap={4}>
                                    <h3 className={styles.sectionTitle}>
                                        {SCHEDULE_TEMPLATE_STEPS[0].label}
                                    </h3>
                                    <p className={styles.sectionDescription}>
                                        {SCHEDULE_TEMPLATE_STEPS[0].description}
                                    </p>
                                </VStack>
                                <ScheduleTemplateStepServicePeriod
                                    formik={formik}
                                />
                            </VStack>
                        </section>
                        <section className={styles.formSection}>
                            <VStack gap={16}>
                                <VStack gap={4}>
                                    <h3 className={styles.sectionTitle}>
                                        {SCHEDULE_TEMPLATE_STEPS[1].label}
                                    </h3>
                                    <p className={styles.sectionDescription}>
                                        {SCHEDULE_TEMPLATE_STEPS[1].description}
                                    </p>
                                </VStack>
                                <ScheduleTemplateStepWork formik={formik} />
                            </VStack>
                        </section>
                        <section className={styles.formSection}>
                            <VStack gap={16}>
                                <VStack gap={4}>
                                    <h3 className={styles.sectionTitle}>
                                        {SCHEDULE_TEMPLATE_STEPS[2].label}
                                    </h3>
                                    <p className={styles.sectionDescription}>
                                        {SCHEDULE_TEMPLATE_STEPS[2].description}
                                    </p>
                                </VStack>
                                <ScheduleTemplateStepSchedule formik={formik} />
                            </VStack>
                        </section>
                    </VStack>
                </div>
                <div className={styles.right}>
                    <VStack gap={6} className={styles.rightHeader}>
                        <h3 className={styles.rightTitle}>
                            Upcoming applications
                        </h3>
                        <p className={styles.rightDescription}>
                            Preview of the next occurrences this schedule will
                            create, based on the recurrence settings on the
                            left.
                        </p>
                    </VStack>
                    <div className={styles.rightTable}>
                        <ServiceTemplateScheduleOccurrencesTable
                            rows={occurrenceRows}
                            emptyMessage="Set up the schedule on the left to preview upcoming applications here."
                        />
                    </div>
                </div>
            </div>
            <div className={styles.commandBar}>
                <CommandIsland alignEnd className={styles.commandIsland}>
                    <ButtonWithLoader
                        variant="secondary"
                        size="md"
                        type="button"
                        loading={false}
                        onClick={onBack}
                    >
                        Back
                    </ButtonWithLoader>
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        type="button"
                        loading={false}
                        onClick={onContinue}
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            description: "Continue",
                            preventDefault: true,
                        }}
                        hotKeyDisplayPosition="inline"
                    >
                        Continue
                    </ButtonWithLoader>
                </CommandIsland>
            </div>
        </div>
    );
};
